import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { WaitingSpinner, GameEndedScreen, LoadingQuestion, TimerBar } from '../shared';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import { getGameName } from '../../utils/gameUtils';
import type { GameStatistics } from '../../types/game';
import '../../App.css';
import '../../styles/game-layout.css';
import '../../styles/player-screen.css';
import { useTranslation } from '../../i18n';

type AssociationsRole = 'explainer' | 'guesser';

interface AssociationsOption {
  id: string;
  label: string;
}

interface AssociationsQuestion {
  questionId: string;
  timeLimit: number;
  startTime: number;
  playerId: string;
  role: AssociationsRole;
  teamId: 'A' | 'B';
  canSubmit: boolean;
  word?: string;
  options?: AssociationsOption[];
  explainerName?: string;
  difficulty?: string;
  description?: string;
  teamLocked?: boolean;
  teamSolved?: boolean;
  questionNumber?: number;
  totalQuestions?: number;
}

interface AssociationsPlayerDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string; teamId?: 'A' | 'B' }>;
    teams?: { A: { score: number; name: string }; B: { score: number; name: string } };
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: {
      questionId?: string;
      id?: string;
      question?: string;
      title?: string;
      timeLimit?: number;
      startTime?: number;
      questionNumber?: number;
      totalQuestions?: number;
      isAssociations?: boolean;
      difficulty?: string;
    };
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

const AssociationsPlayerDisplay: React.FC<AssociationsPlayerDisplayProps> = ({
  roomState,
  socket,
  myPlayerId,
  onGoHome,
  onLoadingTimeout,
  isHost = false,
  onBackToGameSelection,
}) => {
  const { t } = useTranslation();
  const [assocQuestion, setAssocQuestion] = useState<AssociationsQuestion | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [myScore, setMyScore] = useState(0);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const isSubmittingRef = useRef(false);
  const currentQuestionIdRef = useRef<string | undefined>(undefined);
  const myTeamIdRef = useRef<'A' | 'B' | undefined>(undefined);
  const gameEndData = useGameEndScreenData(roomState);

  useEffect(() => {
    if (myPlayerId && roomState.players) {
      const me = roomState.players.find((p) => p.id === myPlayerId);
      if (me) {
        setMyScore(me.score);
        if (me.teamId) myTeamIdRef.current = me.teamId;
      }
    }
  }, [roomState.players, myPlayerId]);

  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setAssocQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    } else if (roomState.gameState === 'playing' && gameEnded) {
      setGameEnded(false);
      setAssocQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setSelectedOptionId(null);
      setAnswerSubmitted(false);
      setAnswerCorrect(null);
      currentQuestionIdRef.current = undefined;
    }
  }, [roomState.gameState, gameEndData, gameEnded]);

  useEffect(() => {
    if (!assocQuestion && roomState.currentQuestion?.isAssociations) {
      const q = roomState.currentQuestion;
      const questionId = q.questionId || q.id;
      if (questionId && socket && roomState.roomName) {
        socket.emit('get-associations-state', { roomName: roomState.roomName, questionId });
      }
    }
  }, [roomState.currentQuestion, assocQuestion, socket, roomState.roomName]);

  useEffect(() => {
    const handleQuestion = (data: AssociationsQuestion) => {
      const actualPlayerId = myPlayerId || socket.id;
      if (data.playerId && data.playerId !== actualPlayerId && data.playerId !== socket.id) {
        return;
      }
      currentQuestionIdRef.current = data.questionId;
      myTeamIdRef.current = data.teamId;
      setAssocQuestion(data);
      setSelectedOptionId(null);
      setAnswerSubmitted(!!data.teamLocked);
      setAnswerCorrect(data.teamLocked ? !!data.teamSolved : null);
      setTimeRemaining(data.timeLimit);
      isSubmittingRef.current = false;
    };

    const handleAnswerReceived = (data: { questionId: string; isCorrect: boolean }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      setAnswerSubmitted(true);
      setAnswerCorrect(data.isCorrect);
      isSubmittingRef.current = false;
    };

    const handleTeamLocked = (data: {
      questionId: string;
      teamId: 'A' | 'B';
      isCorrect: boolean;
    }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      if (data.teamId !== myTeamIdRef.current) return;
      setAnswerSubmitted(true);
      setAnswerCorrect(data.isCorrect);
      isSubmittingRef.current = false;
    };

    const handleGameEnded = (data: { leaderboard?: typeof finalLeaderboard; statistics?: GameStatistics }) => {
      setGameEnded(true);
      setAssocQuestion(null);
      setFinalLeaderboard(data.leaderboard || []);
      setGameStatistics(data.statistics || null);
    };

    const handleGameStarted = () => {
      setGameEnded(false);
      setAssocQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setSelectedOptionId(null);
      setAnswerSubmitted(false);
      setAnswerCorrect(null);
      currentQuestionIdRef.current = undefined;
      isSubmittingRef.current = false;
    };

    socket.on('associations-question', handleQuestion);
    socket.on('answer-received', handleAnswerReceived);
    socket.on('associations-team-locked', handleTeamLocked);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);

    return () => {
      socket.off('associations-question', handleQuestion);
      socket.off('answer-received', handleAnswerReceived);
      socket.off('associations-team-locked', handleTeamLocked);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
    };
  }, [socket, myPlayerId]);

  useEffect(() => {
    if (!assocQuestion || answerSubmitted) return;
    const interval = setInterval(() => {
      const elapsed = Date.now() - assocQuestion.startTime;
      const remaining = Math.max(0, assocQuestion.timeLimit - elapsed);
      setTimeRemaining(remaining);
    }, 100);
    return () => clearInterval(interval);
  }, [assocQuestion, answerSubmitted]);

  const handleSubmit = (optionId?: string) => {
    if (!assocQuestion || !assocQuestion.canSubmit || answerSubmitted || isSubmittingRef.current) return;
    const answer = optionId || selectedOptionId;
    if (!answer) return;

    isSubmittingRef.current = true;
    setSelectedOptionId(answer);
    socket.emit('submit-answer', {
      roomName: roomState.roomName,
      questionId: assocQuestion.questionId,
      answer,
      timestamp: Date.now(),
    });
  };

  if (gameEnded) {
    const handlePlaySameGame = () => {
      setGameEnded(false);
      setAssocQuestion(null);
      setFinalLeaderboard([]);
      socket.emit('start-game', { roomName: roomState.roomName, gameType: roomState.gameType });
    };

    const teamLeaderboard = roomState.teams
      ? [
          { playerId: 'A', playerName: roomState.teams.A.name, score: roomState.teams.A.score },
          { playerId: 'B', playerName: roomState.teams.B.name, score: roomState.teams.B.score },
        ].sort((a, b) => b.score - a.score)
      : finalLeaderboard.length > 0
        ? finalLeaderboard
        : (gameEndData?.finalLeaderboard ?? []);

    return (
      <GameEndedScreen
        gameIcon="🎙️"
        finalLeaderboard={teamLeaderboard}
        myPlayerId={myPlayerId}
        myTeamId={myTeamIdRef.current ?? roomState.players.find((p) => p.id === myPlayerId)?.teamId}
        myScore={myScore}
        isHost={isHost}
        onGoHome={onGoHome || (() => {})}
        onPlayAgain={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection}
        showPositionHighlight
        statistics={gameStatistics ?? gameEndData?.gameStatistics ?? undefined}
        feedbackConfig={{ roomName: roomState.roomName, gameType: roomState.gameType, socket }}
      />
    );
  }

  if (!assocQuestion) {
    if (roomState.currentQuestion) {
      return (
        <LoadingQuestion
          socket={socket}
          roomName={roomState.roomName}
          title={t('gameplay.loading.question')}
          onGoHome={onGoHome}
          onLongWaitTimeout={onLoadingTimeout}
        />
      );
    }
    return (
      <div className="app">
        <Header
          leftButton={onGoHome ? { icon: HeaderIcons.home, onClick: onGoHome, tooltip: t('common.home') } : undefined}
          roomCode={roomState.roomName}
        />
        <div className="game-player-wrapper">
          <WaitingSpinner message={t('gameplay.associations.waiting')} isLoading />
        </div>
      </div>
    );
  }

  const teamScore = roomState.teams?.[assocQuestion.teamId]?.score ?? 0;
  const roleLabel =
    assocQuestion.role === 'explainer'
      ? t('gameplay.associations.roleExplainer')
      : t('gameplay.associations.roleGuesser');
  const gameDisplayName = getGameName(roomState.gameType);

  return (
    <div className="app">
      <Header
        leftButton={{
          icon: HeaderIcons.home,
          onClick: () => onGoHome?.(),
          tooltip: t('gameplay.leaveGame'),
        }}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage'),
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
            <span className="header-game-name" style={{ fontSize: '1rem' }}>{gameDisplayName}</span>
            <span
              style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                padding: '0.3rem 0.6rem',
                backgroundColor: 'rgba(var(--color-primary-rgb), 0.1)',
                borderRadius: '6px',
                border: '2px solid rgba(var(--color-primary-rgb), 0.35)',
                color: 'var(--text-primary)',
              }}
            >
              {roomState.roomName}
            </span>
          </div>
        }
      />

      <div className="game-player-wrapper">
        <div className="container player-content-wrapper" style={{ paddingTop: '3rem', width: '100%', maxWidth: '100%' }}>
          <TimerBar
            timeRemaining={timeRemaining}
            timeLimit={assocQuestion.timeLimit}
            questionNumber={assocQuestion.questionNumber}
            totalQuestions={assocQuestion.totalQuestions}
            showTimeCounter={false}
          />

          <div className="question-container" style={{ marginBottom: '1rem', textAlign: 'center' }}>
            <div
              style={{
                display: 'inline-block',
                marginBottom: '0.75rem',
                padding: '0.25rem 0.75rem',
                borderRadius: '999px',
                fontSize: '0.85rem',
                fontWeight: 700,
                color: 'var(--color-primary)',
                background: 'rgba(var(--color-primary-rgb), 0.12)',
                border: '1px solid rgba(var(--color-primary-rgb), 0.3)',
              }}
            >
              {roleLabel}
            </div>
            <p
              style={{
                margin: '0.75rem 0 0',
                fontSize: '0.95rem',
                fontWeight: 700,
                color: 'var(--color-primary)',
              }}
            >
              {t('gameplay.associations.teamScore', { team: assocQuestion.teamId, score: teamScore })}
            </p>
          </div>

          {assocQuestion.role === 'explainer' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
              <div
                style={{
                  fontSize: 'clamp(2rem, 8vw, 3.25rem)',
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  color: 'var(--text-primary)',
                  textAlign: 'center',
                  padding: '1.5rem 1.25rem',
                  width: '100%',
                  maxWidth: '360px',
                  background: 'linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.08) 0%, rgba(var(--color-secondary-rgb), 0.14) 100%)',
                  border: '3px solid rgba(var(--color-secondary-rgb), 0.28)',
                  borderRadius: '15px',
                }}
              >
                {assocQuestion.word}
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', textAlign: 'center', margin: 0, maxWidth: '320px' }}>
                {t('gameplay.associations.explainerHint')}
              </p>
              {answerSubmitted && (
                <p
                  style={{
                    textAlign: 'center',
                    color: answerCorrect ? 'var(--color-success)' : 'var(--color-error)',
                    fontWeight: 700,
                  }}
                >
                  {answerCorrect
                    ? t('gameplay.associations.teamSolved')
                    : t('gameplay.associations.teamMissed')}
                </p>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {assocQuestion.explainerName && (
                <p style={{ textAlign: 'center', color: 'var(--text-secondary)', margin: '0 0 0.25rem', fontSize: '0.9rem' }}>
                  {t('gameplay.associations.listenTo', { name: assocQuestion.explainerName })}
                </p>
              )}
              {(assocQuestion.options || []).map((option) => {
                const isSelected = selectedOptionId === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    disabled={answerSubmitted}
                    onClick={() => handleSubmit(option.id)}
                    className="button"
                    style={{
                      width: '100%',
                      padding: '1.1rem 1rem',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      color: 'var(--text-primary)',
                      background: isSelected
                        ? 'rgba(var(--color-primary-rgb), 0.22)'
                        : 'rgba(var(--color-primary-rgb), 0.1)',
                      border: isSelected
                        ? '3px solid var(--color-primary)'
                        : '3px solid rgba(var(--color-primary-rgb), 0.35)',
                      borderRadius: '15px',
                      cursor: answerSubmitted ? 'default' : 'pointer',
                      opacity: answerSubmitted && !isSelected ? 0.65 : 1,
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
              {answerSubmitted && (
                <p
                  style={{
                    textAlign: 'center',
                    color: answerCorrect ? 'var(--color-success)' : 'var(--color-error)',
                    fontWeight: 700,
                  }}
                >
                  {answerCorrect
                    ? t('gameplay.associations.correct')
                    : t('gameplay.associations.wrong')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssociationsPlayerDisplay;
